// components/ArchivedAcademicYears.js
import React, { useState, useEffect, useCallback } from 'react';
import moment from 'moment';
import api from '../../../api';

const ArchivedAcademicYears = ({ 
  tenantId, 
  userId, 
  onRestoreAcademicYear,
  onClose,
  onViewCalendar 
}) => {
  const [archivedYears, setArchivedYears] = useState([]);
  const [loading, setLoading] = useState(false);
  const [setError] = useState(null);

  // Memoize fetchArchivedAcademicYears for useEffect
  const fetchArchivedAcademicYears = useCallback(async () => {
    if (!tenantId) return;
    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem('token');
      const response = await api.get(`/api/academic-year/archived/list?tenantId=${tenantId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setArchivedYears(response.data.academicYears || []);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load archived academic years');
    } finally {
      setLoading(false);
    }
  }, [tenantId, setError]);

  useEffect(() => {
    fetchArchivedAcademicYears();
  }, [tenantId, fetchArchivedAcademicYears]); // <-- Added fetchArchivedAcademicYears

  const canRestore = (academicYear) => {
    return academicYear.canRestoreUntil && new Date(academicYear.canRestoreUntil) > new Date();
  };

  const getDaysRemaining = (canRestoreUntil) => {
    const now = moment();
    const restoreUntil = moment(canRestoreUntil);
    return restoreUntil.diff(now, 'days');
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-60 transition-opacity">
      <div className="bg-white rounded-3xl shadow-2xl max-w-6xl w-full mx-4 max-h-[90vh] overflow-hidden transform transition-all">
        <div className="p-8">
          <div className="flex justify-between items-center mb-8">
            <div>
              <h2 className="text-3xl font-bold text-gray-900">Archived Academic Years</h2>
              <p className="text-gray-600 mt-2">
                Restore accidentally archived academic years (Data preserved for 2 years)
              </p>
            </div>
            <button
              onClick={onClose}
              className="p-3 text-gray-400 hover:text-gray-600 transition-colors rounded-2xl hover:bg-gray-100"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
            <div className="bg-blue-50 p-6 rounded-2xl border border-blue-200">
              <div className="text-2xl font-bold text-blue-700 mb-2">{archivedYears.length}</div>
              <div className="text-sm text-blue-600 font-medium">Total Archived</div>
            </div>
            <div className="bg-green-50 p-6 rounded-2xl border border-green-200">
              <div className="text-2xl font-bold text-green-700 mb-2">
                {archivedYears.filter(ay => canRestore(ay)).length}
              </div>
              <div className="text-sm text-green-600 font-medium">Can Be Restored</div>
            </div>
            <div className="bg-orange-50 p-6 rounded-2xl border border-orange-200">
              <div className="text-2xl font-bold text-orange-700 mb-2">
                {archivedYears.filter(ay => !canRestore(ay)).length}
              </div>
              <div className="text-sm text-orange-600 font-medium">Expired</div>
            </div>
          </div>

          {/* Archived Years List */}
          {loading ? (
            <div className="flex justify-center items-center py-20">
              <div className="text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-600 mx-auto mb-4"></div>
                <p className="text-gray-600">Loading archived academic years...</p>
              </div>
            </div>
          ) : archivedYears.length === 0 ? (
            <div className="text-center py-20">
              <div className="w-24 h-24 bg-gray-100 rounded-3xl flex items-center justify-center mx-auto mb-6">
                <svg className="w-12 h-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4" />
                </svg>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-3">No Archived Academic Years</h3>
              <p className="text-gray-600 mb-8">
                There are no archived academic years to display.
              </p>
            </div>
          ) : (
            <div className="space-y-6 max-h-96 overflow-y-auto">
              {archivedYears.map((academicYear) => (
                <div
                  key={academicYear.id || academicYear._id}
                  className="bg-orange-50 border border-orange-200 rounded-2xl p-6"
                >
                  <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-4 mb-3">
                        <h3 className="text-xl font-bold text-gray-900">{academicYear.name}</h3>
                        <span className="px-3 py-1 bg-orange-100 text-orange-800 text-sm font-medium rounded-full border border-orange-200">
                          📁 Archived
                        </span>
                        {canRestore(academicYear) ? (
                          <span className="px-3 py-1 bg-green-100 text-green-800 text-sm font-medium rounded-full border border-green-200">
                            🔄 {getDaysRemaining(academicYear.canRestoreUntil)} days remaining
                          </span>
                        ) : (
                          <span className="px-3 py-1 bg-red-100 text-red-800 text-sm font-medium rounded-full border border-red-200">
                            ⏰ Restoration expired
                          </span>
                        )}
                      </div>
                      <p className="text-gray-600 mb-2">
                        {moment(academicYear.startDate).format('MMM DD, YYYY')} - {moment(academicYear.endDate).format('MMM DD, YYYY')}
                      </p>
                      <p className="text-sm text-orange-600">
                        Archived on {moment(academicYear.archivedAt).format('MMM DD, YYYY')} • 
                        {academicYear.archiveReason && ` Reason: ${academicYear.archiveReason}`}
                      </p>
                      {academicYear.canRestoreUntil && (
                        <p className="text-sm text-gray-500 mt-1">
                          Can restore until {moment(academicYear.canRestoreUntil).format('MMM DD, YYYY')}
                        </p>
                      )}
                    </div>

                    <div className="flex gap-3">
                      {canRestore(academicYear) ? (
                        <button
                          onClick={() => onRestoreAcademicYear(academicYear.id || academicYear._id)}
                          disabled={loading}
                          className="flex items-center gap-2 px-6 py-3 bg-green-500 text-white rounded-xl hover:bg-green-600 transition-all duration-200 font-semibold"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                          </svg>
                          Restore
                        </button>
                      ) : (
                        <button
                          disabled
                          className="flex items-center gap-2 px-6 py-3 bg-gray-300 text-gray-500 rounded-xl cursor-not-allowed font-semibold"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                          </svg>
                          Expired
                        </button>
                      )}
                      <button
                        onClick={() => {
                          if (typeof onViewCalendar === 'function') {
                            onViewCalendar(academicYear);
                          } else {
                            console.log('ArchivedAcademicYears: onViewCalendar not provided — falling back to onClose');
                            onClose();
                          }
                        }}
                        className="flex items-center gap-2 px-6 py-3 bg-blue-500 text-white rounded-xl hover:bg-blue-600 transition-all duration-200 font-semibold"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                        View Calendar
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          <div className="mt-8 flex justify-between items-center pt-6 border-t border-gray-200">
            <p className="text-sm text-gray-600">
              💡 Archived academic years are preserved for 2 years. After that, they are permanently deleted.
            </p>
            <button
              onClick={onClose}
              className="px-6 py-3 bg-gray-100 text-gray-700 rounded-xl hover:bg-gray-200 transition-all duration-200 font-semibold"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ArchivedAcademicYears;